'use client'

import React, { useEffect, useMemo, useState } from 'react';
import { DatePicker } from 'antd';
import { useDispatch } from 'react-redux';
import { formatMsg, Helper, attendanceActions, packageActions, multicenterActions, accountActions, timeutil, notification, SpinWrapper } from 'sharedresources';
import SlotTimerPill from '../SlotTimerPill';
import {
    CHILD_TINTS, NextAction, ParentSession, buildSessions, getDaySummary, getInitials, getNextAction, isCancelledEvent,
    getParentMarkSetting, groupTodaySessions,
} from './parentAttendance';
import { AbsentSheet, PresentSheet, TimeSheet } from './Sheets';
import styles from './style.module.css';

const { getAllEventsOfParticipants, getParticipantsDateRangeAttendanceByAccountId, markParentAttendance, resetOperationType, resetAttendanceReducer, defaultAttendanceReducerState } = attendanceActions;
const { getPackageListDropdown } = packageActions;
const { getAllCentersList } = multicenterActions;
const { getUsersOfAccount } = accountActions;

// Sections move as the clock does (live → needs you); minutes are enough.
const CLOCK_TICK_MS = 30000;

type SheetState =
    | { type: 'time'; session: ParentSession; field: 'in' | 'out' }
    | { type: 'present'; session: ParentSession }
    | { type: 'absent'; session: ParentSession }
    | null;

const fmtTime = (epoch: number) => timeutil.getInstance(epoch).format('h:mm A');

const ParentAttendance = () => {
    const dispatch = useDispatch();
    const { firebase, attendanceReducer, accountReducer, packageReducer, multicenterReducer } =
        Helper.getSelectorState(['attendanceReducer', 'accountReducer', 'packageReducer', 'multicenterReducer']);
    const { allParticipantsEvents, participantsAttendanceMap, operationType, parentMarking, isLoading } = attendanceReducer;

    const settings = firebase?.schoolConfig?.studentAttendanceMode;
    const setting = getParentMarkSetting(settings);
    const accountId = firebase?.user?.id;

    const [day, setDay] = useState(() => timeutil.getInstance().startOf('day').valueOf());
    const [now, setNow] = useState(() => timeutil.getEpoch());
    const [sheet, setSheet] = useState<SheetState>(null);
    const [pendingToast, setPendingToast] = useState<string | null>(null);

    const isToday = timeutil.getInstance(day).isSame(timeutil.getInstance(now), 'day');

    useEffect(() => {
        dispatch(getPackageListDropdown({ firebase, status: 'ACTIVE' }));
        dispatch(getAllCentersList({ firebase }));
        dispatch(getUsersOfAccount({ firebase }));
        const id = setInterval(() => setNow(timeutil.getEpoch()), CLOCK_TICK_MS);
        return () => {
            clearInterval(id);
            dispatch(resetAttendanceReducer(defaultAttendanceReducerState));
        };
    }, []);

    const loadDay = () => {
        if (!firebase || !accountId) return;
        const startDate = timeutil.getInstance(day).startOf('day').valueOf();
        const endDate = timeutil.getInstance(day).endOf('day').valueOf();
        dispatch(getAllEventsOfParticipants({ firebase, startDate, endDate, accountId }));
        dispatch(getParticipantsDateRangeAttendanceByAccountId({ firebase, startDate, endDate, accountId }));
    };

    useEffect(loadDay, [firebase, day]);

    useEffect(() => {
        if (operationType === 'PARENT_ATTENDANCE_MARKED') {
            if (pendingToast) notification('success', pendingToast);
            setSheet(null);
            loadDay();
        }
        if (operationType === 'PARENT_ATTENDANCE_MARKED' || operationType === 'PARENT_ATTENDANCE_MARKING_FAILED') {
            setPendingToast(null);
            // A refused mark usually means the record changed elsewhere; show what is there now.
            if (operationType === 'PARENT_ATTENDANCE_MARKING_FAILED') loadDay();
            dispatch(resetOperationType(null));
        }
    }, [operationType]);

    const children = accountReducer.accountUsersList || [];
    const solo = children.length <= 1;
    const childOf = useMemo(() => {
        const map = new Map();
        children.forEach((child, i) => map.set(child.id, { ...child, tint: CHILD_TINTS[i % CHILD_TINTS.length] }));
        return map;
    }, [children]);
    const packageName = useMemo(() => {
        const map = new Map();
        (packageReducer.packageList || []).forEach((pkg) => map.set(pkg.id, pkg.name));
        return map;
    }, [packageReducer.packageList]);
    const centerName = useMemo(() => {
        const map = new Map();
        (multicenterReducer.allCentersList || []).forEach((center) => map.set(center.id, center.name));
        return map;
    }, [multicenterReducer.allCentersList]);

    const sessions = useMemo(
        () => buildSessions(allParticipantsEvents, participantsAttendanceMap),
        [allParticipantsEvents, participantsAttendanceMap],
    );
    const groups = useMemo(() => groupTodaySessions(sessions, now, setting), [sessions, now, setting]);
    const summary = getDaySummary(sessions, setting, isToday);

    const childName = (s: ParentSession) => childOf.get(s.event.profileId)?.name || '';
    const className = (s: ParentSession) => packageName.get(s.event.packageId) || s.event.batchName || '';
    const who = (s: ParentSession) => (solo ? className(s) : childName(s));

    // ---------- marking ----------

    const submit = (session: ParentSession, action: 'CHECKIN' | 'CHECKOUT' | 'PRESENT' | 'ABSENT', toast: string, extra: Record<string, any> = {}) => {
        setPendingToast(toast);
        dispatch(markParentAttendance({
            firebase,
            bookingId: session.event.bookingId,
            slotRandomId: session.event.slotRandomId,
            action,
            ...extra,
        }));
    };

    const onSaveTime = (session: ParentSession, field: 'in' | 'out', time: number) => {
        // In "current time only" the server records its own now; a custom time is sent as picked.
        const extra = setting === 'custom' ? { time } : {};
        if (field === 'in') submit(session, 'CHECKIN', formatMsg('parentAttendance.toastCheckedIn', { name: who(session), time: fmtTime(time) }), extra);
        else submit(session, 'CHECKOUT', formatMsg('parentAttendance.toastCheckedOut', { name: who(session), time: fmtTime(time) }), extra);
    };

    // ---------- rendering helpers ----------

    const chipsFor = (s: ParentSession, ended: boolean) => {
        const chips: { text: string; tone: string }[] = [];
        const a = s.attendance || {};
        if (s.state === 'ABSENT') {
            chips.push({ text: a.absentNote ? formatMsg('parentAttendance.absentWithNote', { note: a.absentNote }) : formatMsg('parentAttendance.absent'), tone: styles.chipRed });
            return chips;
        }
        if (setting === 'pa' || (setting === 'off' && settings?.type !== 'checkin_checkout')) {
            if (s.state !== 'PENDING') chips.push({ text: formatMsg('parentAttendance.present'), tone: styles.chipGreen });
        } else {
            const tone = s.state === 'PRESENT' || !ended ? styles.chipGreen : styles.chipOrange;
            // A late check-in or check-out is one pill with its time, e.g. "In 6:06 PM · Late".
            if (typeof a.latestCheckIn === 'number') {
                chips.push(a.lateIn
                    ? { text: formatMsg('parentAttendance.inAtLate', { time: fmtTime(a.latestCheckIn) }), tone: styles.chipLate }
                    : { text: formatMsg('parentAttendance.inAt', { time: fmtTime(a.latestCheckIn) }), tone });
            }
            if (typeof a.latestCheckOut === 'number') {
                chips.push(a.lateOut
                    ? { text: formatMsg('parentAttendance.outAtLate', { time: fmtTime(a.latestCheckOut) }), tone: styles.chipLate }
                    : { text: formatMsg('parentAttendance.outAt', { time: fmtTime(a.latestCheckOut) }), tone: styles.chipGreen });
            }
            if (s.state === 'CHECKED_IN' && ended) chips.push({ text: formatMsg('parentAttendance.noCheckOut'), tone: styles.chipOrange });
        }
        if (s.state === 'PENDING' && (ended || setting === 'off')) {
            chips.push(setting === 'off' || !isToday
                ? { text: formatMsg('parentAttendance.notMarkedYet'), tone: styles.chipGrey }
                : { text: formatMsg('parentAttendance.notMarked'), tone: styles.chipOrange });
        }
        return chips;
    };

    const buttonsFor = (s: ParentSession) => {
        const action: NextAction = getNextAction(s.state, setting, isToday, isCancelledEvent(s.event));
        const absentBtn = { key: 'absent', label: formatMsg('parentAttendance.cantMakeIt'), primary: false, onClick: () => setSheet({ type: 'absent', session: s }) };
        switch (action) {
            case 'MARK':
                return [absentBtn, { key: 'present', label: formatMsg('parentAttendance.present'), primary: true, onClick: () => setSheet({ type: 'present', session: s }) }];
            case 'CHECKIN':
                return [absentBtn, { key: 'in', label: formatMsg('parentAttendance.checkIn'), primary: true, onClick: () => setSheet({ type: 'time', session: s, field: 'in' }) }];
            case 'CHECKOUT':
                return [{ key: 'out', label: formatMsg('parentAttendance.checkOut'), primary: true, onClick: () => setSheet({ type: 'time', session: s, field: 'out' }) }];
            default:
                return [];
        }
    };

    const timerFor = (s: ParentSession) => (
        <SlotTimerPill
            record={s.event}
            attendance={s.attendance}
            status={s.state === 'ABSENT' || s.state === 'PENDING' ? s.state : 'PRESENT'}
            settings={settings}
            showEndsAt={false}
            className={styles.timerPill}
        />
    );

    const renderAvatar = (s: ParentSession, size: 'lg' | 'md' | 'sm') => {
        if (solo) return null;
        const child = childOf.get(s.event.profileId);
        const tint = child?.tint || CHILD_TINTS[0];
        return (
            <span className={`${styles.avatar} ${styles[`avatar_${size}`]}`} style={{ background: tint.bg, color: tint.fg }} aria-hidden="true">
                {getInitials(child?.name)}
            </span>
        );
    };

    const renderIdentity = (s: ParentSession, ended = false) => (
        <div className={styles.identity}>
            <div className={styles.name}>{solo ? className(s) : childName(s)}</div>
            <div className={styles.line2}>
                {solo ? '' : `${className(s)} · `}{fmtTime(s.start)} – {fmtTime(s.end)}
                {ended ? ` · ${formatMsg('parentAttendance.endedAt', { time: fmtTime(s.end) })}` : ''}
            </div>
            {centerName.get(s.event.centerId) && (
                <div className={styles.center}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                    {centerName.get(s.event.centerId)}
                </div>
            )}
        </div>
    );

    const renderChips = (s: ParentSession, ended: boolean) => {
        const chips = chipsFor(s, ended);
        return (
            <div className={styles.chips}>
                {timerFor(s)}
                {chips.map((chip) => <span key={chip.text} className={`${styles.chip} ${chip.tone}`}>{chip.text}</span>)}
            </div>
        );
    };

    const renderButtons = (s: ParentSession) => {
        const buttons = buttonsFor(s);
        if (!buttons.length) return null;
        return (
            <div className={styles.buttons}>
                {buttons.map((b) => (
                    <button
                        key={b.key}
                        type="button"
                        className={`${styles.btn} ${b.primary ? styles.btnPrimary : styles.btnGhost}`}
                        onClick={b.onClick}
                        disabled={parentMarking}
                        data-testid={`parent-attendance-${b.key}`}
                    >
                        {b.label}
                    </button>
                ))}
            </div>
        );
    };

    const renderCard = (s: ParentSession, kind: 'hero' | 'needs' | 'upcoming') => (
        <div key={s.key} className={`${styles.card} ${kind === 'hero' ? styles.hero : ''} ${kind === 'needs' && setting !== 'off' ? styles.cardNeeds : ''}`} data-testid="parent-attendance-card">
            {kind === 'hero' && (
                <span className={styles.liveBadge}><span className={styles.liveDot} />{formatMsg('parentAttendance.happeningNow')}</span>
            )}
            <div className={styles.cardMain}>
                {renderAvatar(s, kind === 'hero' ? 'lg' : 'md')}
                <div className={styles.cardBody}>
                    {renderIdentity(s, kind === 'needs')}
                    {renderChips(s, kind === 'needs')}
                </div>
            </div>
            {renderButtons(s)}
        </div>
    );

    const renderCompact = (s: ParentSession) => (
        <div key={s.key} className={styles.compact} data-testid="parent-attendance-card">
            {renderAvatar(s, 'sm')}
            {renderIdentity(s)}
            <div className={styles.compactChips}>
                {chipsFor(s, true).map((chip) => <span key={chip.text} className={`${styles.chip} ${chip.tone}`}>{chip.text}</span>)}
            </div>
        </div>
    );

    const renderSection = (title: string, count: number | undefined, body: React.ReactNode) => (
        <section className={styles.section}>
            <div className={styles.sectionLabel}>
                <span>{title}</span>{typeof count === 'number' && <span className={styles.sectionCount}>{count}</span>}
            </div>
            <div className={styles.sectionBody}>{body}</div>
        </section>
    );

    // ---------- page ----------

    const dateLabel = timeutil.getInstance(day).format('DD MMM');
    const title = isToday
        ? (solo && children[0] ? formatMsg('parentAttendance.titleSoloToday', { name: children[0].name, date: dateLabel }) : formatMsg('parentAttendance.titleToday', { date: dateLabel }))
        : timeutil.getInstance(day).format('ddd, DD MMM YYYY');
    const subtitle = solo
        ? formatMsg('parentAttendance.subSolo', { sessions: sessions.length })
        : formatMsg('parentAttendance.subMulti', { children: children.length, sessions: sessions.length });

    const loading = isLoading && !allParticipantsEvents;

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div>
                    <h1 className={styles.title}>{title}</h1>
                    <p className={styles.subtitle}>{subtitle}</p>
                </div>
                <div className={styles.dateControls}>
                    {!isToday && (
                        <button type="button" className={`${styles.btn} ${styles.btnGhost} ${styles.btnSmall}`} onClick={() => setDay(timeutil.getInstance().startOf('day').valueOf())}>
                            {formatMsg('parentAttendance.today')}
                        </button>
                    )}
                    <DatePicker
                        allowClear={false}
                        value={timeutil.getInstance(day)}
                        format={Helper.dateFormat(firebase)}
                        onChange={(value) => value && setDay(timeutil.getInstance(value).startOf('day').valueOf())}
                        disabledDate={(current) => current && current > timeutil.getInstance().endOf('day')}
                        popupClassName='attn-today-btn'
                        className={styles.datePicker}
                        aria-label={formatMsg('parentAttendance.pickDate')}
                        data-testid="parent-attendance-date"
                    />
                </div>
            </div>

            <div className={styles.layout}>
                <main className={styles.main}>
                    {setting === 'off' && (
                        <div className={styles.banner}>{formatMsg('parentAttendance.viewOnlyBanner')}</div>
                    )}
                    {setting !== 'off' && !isToday && (
                        <div className={styles.banner}>{formatMsg('parentAttendance.pastDayBanner')}</div>
                    )}

                    <SpinWrapper spinning={loading}>
                        {!loading && sessions.length === 0 && (
                            <div className={styles.empty}>{formatMsg('parentAttendance.noSessions')}</div>
                        )}

                        {isToday ? (
                            <>
                                {groups.live.map((s) => renderCard(s, 'hero'))}
                                {groups.needs.length > 0 && renderSection(
                                    formatMsg(setting === 'off' ? 'parentAttendance.earlierToday' : 'parentAttendance.needsYou'),
                                    groups.needs.length,
                                    groups.needs.map((s) => renderCard(s, 'needs')),
                                )}
                                {groups.upcoming.length > 0 && renderSection(
                                    formatMsg('parentAttendance.upNext'),
                                    groups.upcoming.length,
                                    groups.upcoming.map((s) => renderCard(s, 'upcoming')),
                                )}
                                {groups.settled.length > 0 && renderSection(
                                    formatMsg('parentAttendance.settled'),
                                    undefined,
                                    groups.settled.map(renderCompact),
                                )}
                            </>
                        ) : (
                            sessions.length > 0 && renderSection(
                                formatMsg('parentAttendance.sessions'),
                                sessions.length,
                                sessions.map(renderCompact),
                            )
                        )}
                    </SpinWrapper>
                </main>

                {sessions.length > 0 && (
                    <aside className={styles.summary} aria-label={formatMsg('parentAttendance.summary')}>
                        {isToday && setting !== 'off' && (
                            <div className={styles.stat}>
                                <span className={styles.statValue}>{summary.toDo}</span>
                                <span className={styles.statLabel}>{formatMsg('parentAttendance.toDoNow')}</span>
                            </div>
                        )}
                        <div className={styles.stat}>
                            <span className={`${styles.statValue} ${styles.statGreen}`}>{summary.done}</span>
                            <span className={styles.statLabel}>{formatMsg('parentAttendance.done')}</span>
                        </div>
                        <div className={styles.stat}>
                            <span className={`${styles.statValue} ${styles.statRed}`}>{summary.absent}</span>
                            <span className={styles.statLabel}>{formatMsg('parentAttendance.absent')}</span>
                        </div>
                    </aside>
                )}
            </div>

            {sheet?.type === 'time' && (
                <TimeSheet
                    session={sheet.session}
                    field={sheet.field}
                    editable={setting === 'custom'}
                    now={timeutil.getEpoch()}
                    title={formatMsg(sheet.field === 'in' ? 'parentAttendance.checkInTitle' : 'parentAttendance.checkOutTitle', { name: solo ? '' : childName(sheet.session) }).trim()}
                    subtitle={`${solo ? '' : `${childName(sheet.session)} · `}${className(sheet.session)} · ${fmtTime(sheet.session.start)} – ${fmtTime(sheet.session.end)}`}
                    saving={parentMarking}
                    onClose={() => setSheet(null)}
                    onSave={(time) => onSaveTime(sheet.session, sheet.field, time)}
                />
            )}
            {sheet?.type === 'present' && (
                <PresentSheet
                    title={formatMsg('parentAttendance.presentTitle', { name: who(sheet.session) })}
                    subtitle={`${className(sheet.session)} · ${fmtTime(sheet.session.start)} – ${fmtTime(sheet.session.end)}`}
                    saving={parentMarking}
                    onClose={() => setSheet(null)}
                    onConfirm={() => submit(sheet.session, 'PRESENT', formatMsg('parentAttendance.toastPresent', { name: who(sheet.session) }))}
                />
            )}
            {sheet?.type === 'absent' && (
                <AbsentSheet
                    title={formatMsg('parentAttendance.absentTitle', { name: who(sheet.session) })}
                    subtitle={`${className(sheet.session)} · ${fmtTime(sheet.session.start)} – ${fmtTime(sheet.session.end)}`}
                    askReason={!!settings?.enableAbsentNotes}
                    saving={parentMarking}
                    onClose={() => setSheet(null)}
                    onConfirm={(note) => submit(sheet.session, 'ABSENT', formatMsg('parentAttendance.toastAbsent', { name: who(sheet.session) }), { absentNote: note })}
                />
            )}
        </div>
    );
};

export default ParentAttendance;
